import { FormInstance, message } from 'antd'
import { isEqual } from 'lodash'
import _ from 'lodash'
import { PropertyType, RentalInfoPeriod } from 'Modules'
import { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { PTOTypeEnum, TermsFlowEnum, TokenizeStatusEnum } from 'constants/pto'
import { SellingStatusEnum } from 'constants/pto-v2'
import { usePTOV2Context } from 'context/pto-v2-context-provider'
import { toRentalPeriodPayload } from 'extends/pto.extend'
import {
  cleanCoTrustedPartners,
  cleanGoldenTickets,
  cleanMetadata,
  INITIAL_CO_TRUSTED_PARTNER,
  INITIAL_GOLDEN_TICKET,
  normalizeTermsContent,
} from 'extends/pto-v2.extend'
import usePrevious from 'hooks/use-previous'
import { convertPercentContract } from 'utils/contract'
import { logError, logInfo } from 'utils/logger'

interface UseEditPTOV2Props {
  form: FormInstance
}

/**
 * PTO v2 edit flow: price/fee/timelines are managed on the Selling Details tab,
 * so Basic Information no longer initializes or submits them.
 */
const useEditPTOV2 = ({ form }: UseEditPTOV2Props) => {
  const [edit, setEdit] = useState(false)
  const [thumbnail, setThumbnail] = useState<string>('')
  const [thumbnails, setThumbnails] = useState<string[]>([])
  const [defaultPropertyType, setDefaultPropertyType] = useState<PropertyType | null>(null)
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([])
  const [switchChecked, setSwitchChecked] = useState(false)

  const { code, ptoDetail, getPTODetail } = usePTOV2Context()
  const previewPtoDetail = usePrevious(ptoDetail)

  const initFormPTODetail = useCallback(async () => {
    try {
      if (!code || !ptoDetail) return
      setSwitchChecked(ptoDetail.status === TokenizeStatusEnum.ACTIVE)
      const resGetListProperty = await AppAdmin.getListProperty()
      const types: PropertyType[] = []
      if (resGetListProperty?.data.success) {
        resGetListProperty?.data.data.map((item: PropertyType) => {
          types.push({
            ...item,
            id: Number(item.id),
          } as PropertyType)
        })
        setPropertyTypes(types)
      }
      const propertyItems: any = {}
      const selectedType = types.find((type: any) => type.id === ptoDetail?.type)
      if (selectedType) {
        setDefaultPropertyType(selectedType)
        selectedType.properties?.map((prop: any) => {
          propertyItems[`${prop}`] = ptoDetail?.propertyItems?.[`${prop}`]
        })
      }

      const formValues = {
        code,
        name: ptoDetail?.fullName,
        contractName: ptoDetail?.contractName,
        terms: normalizeTermsContent(ptoDetail?.terms),
        type: ptoDetail?.type,
        country: ptoDetail?.country,
        contractAddress: ptoDetail?.contractAddress,
        ownerAddress: ptoDetail?.ownerAddress,
        description: ptoDetail?.description,
        latitude: ptoDetail?.latitude,
        longitude: ptoDetail?.longitude,
        status: ptoDetail?.status === TokenizeStatusEnum.ACTIVE,
        marketPlaceSC: ptoDetail?.marketPlaceSC,
        isFeatured: ptoDetail?.isFeatured,
        isShowDetail: ptoDetail.isShowDetail,
        isShowList: ptoDetail.isShowList,
        propertyItems: propertyItems,
        metadata: !_.isEmpty(ptoDetail?.metadata) ? ptoDetail?.metadata : {},
        metadataUrl: ptoDetail?.metadataUrl,
        marketplaceEnable: ptoDetail?.marketplaceEnable || false,
        tradeUnlockTime: ptoDetail?.tradeUnlockTime,
        maxNFTSupply: ptoDetail?.maxNFTSupply,
        // v2 API returns royaltyFee in basis points (250 = 2.5%)
        royalty: convertPercentContract(ptoDetail?.royaltyFee),
        goldenTickets: ptoDetail?.goldenTickets?.map((item: any) => ({
          ...item,
          isNewRecord: false,
        })) || [INITIAL_GOLDEN_TICKET],
        typeSocial: ptoDetail?.typeSocial || {},
        ptoType: ptoDetail?.ptoType,
        rentalPeriods: ptoDetail?.revenueSharingRental || [],
        isLeasingInProgress: ptoDetail?.isLeasingInProgress || false,
        coTrustedPartners: ptoDetail?.coTrustedPartners?.length
          ? ptoDetail.coTrustedPartners
          : [INITIAL_CO_TRUSTED_PARTNER],
      }

      logInfo('formValues', formValues)

      form.setFieldsValue(formValues)

      setThumbnail(ptoDetail.thumbnail)
      setThumbnails(ptoDetail.thumbnails)
    } catch (error) {
      logError('Error fetching details: ', error)
    }
  }, [code, form, ptoDetail])

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const {
        code,
        type,
        name,
        contractName,
        country,
        description = '',
        latitude,
        isShowDetail,
        isShowList,
        longitude,
        status,
        isFeatured,
        propertyItems,
        marketplaceEnable,
        metadata: formMetadata,
        tradeUnlockTime,
        maxNFTSupply,
        royalty,
        ownerAddress,
        goldenTickets: formGoldenTickets,
        typeSocial,
        rentalPeriods,
        isLeasingInProgress,
        ptoType,
        terms,
        coTrustedPartners: formCoTrustedPartners,
      } = values

      const metadata = cleanMetadata(formMetadata)
      const goldenTickets = cleanGoldenTickets(formGoldenTickets)
      const coTrustedPartners = cleanCoTrustedPartners(formCoTrustedPartners)

      // Body of PATCH /admin/pto/v2/:id - contractAddress is managed via
      // PATCH /admin/pto/:id/contract instead
      const params = {
        fullName: name,
        contractName: (contractName || '').trim(),
        sellingStatus: ptoDetail?.sellingStatus ?? SellingStatusEnum.OFF_SALE,
        status: status ? TokenizeStatusEnum.ACTIVE : TokenizeStatusEnum.INACTIVE,
        country: country,
        description: description,
        isFeatured: !!isFeatured,
        isShowDetail,
        isShowList,
        ptoType: ptoType,
        type: type,
        latitude: latitude,
        longitude: longitude,
        propertyItems: propertyItems,
        marketplaceEnable: marketplaceEnable ? Boolean(marketplaceEnable) || false : false,
        thumbnail: thumbnail || '',
        thumbnails: thumbnails || [],
        metadata: metadata,
        ownerAddress: ownerAddress,
        tradeUnlockTime: tradeUnlockTime || 0,
        maxNFTSupply,
        royaltyFee: royalty,
        goldenTickets,
        typeSocial: typeSocial || {},
        isLeasingInProgress: !!isLeasingInProgress,
        terms: {
          flowType: TermsFlowEnum.PURCHASE,
          content: terms || '',
        },
        coTrustedPartners,
      }
      await AppAdmin.ptoV2Update(code, params)

      if (ptoType === PTOTypeEnum.RENTAL_PTO && rentalPeriods?.length) {
        const originalPeriods = ptoDetail?.revenueSharingRental || []

        for (const period of rentalPeriods as RentalInfoPeriod[]) {
          const payload = toRentalPeriodPayload(period)

          if (period._id) {
            const original = originalPeriods.find((p: RentalInfoPeriod) => p._id === period._id)
            if (!isEqual(original, period)) {
              const { _id, ...updatePayload } = payload

              const finalPayload = period.contractAddress
                ? { annualNetCashflowToInvestor: updatePayload.annualNetCashflowToInvestor }
                : updatePayload
              await AppAdmin.updateRentalInfo(code, period._id, finalPayload)
            }
          } else {
            await AppAdmin.createRentalInfo(code, payload)
          }
        }
      }

      message.success('PTO updated successfully!')
      getPTODetail()
      setEdit(false)
    } catch (error: any) {
      message.error(error?.message ? error?.message : 'Error while submitting form')
      logError('Error while submitting form:', error)
    }
  }

  const handleCancel = () => {
    initFormPTODetail()
    setEdit(false)
  }

  const handleEdit = () => {
    setEdit(true)
  }

  useEffect(() => {
    if (ptoDetail && !isEqual(ptoDetail, previewPtoDetail)) {
      initFormPTODetail()
    }
  }, [initFormPTODetail, previewPtoDetail, ptoDetail])

  return {
    edit,
    thumbnail,
    setThumbnail,
    thumbnails,
    setThumbnails,
    defaultPropertyType,
    propertyTypes,
    switchChecked,
    setSwitchChecked,
    ptoDetail,
    code,
    handleSubmit,
    handleCancel,
    handleEdit,
  }
}

export default useEditPTOV2
